// src/components/DeepFikaFeedback.jsx
// Feedback form for Deep Fika 1-on-1 sessions
// URL: /deep-fika/feedback?session=...&participant=...

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function callAdminAPI(action, data) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/curated-fika-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ action, data }),
  });
  return res.json();
}

// ── Star Rating ───────────────────────────────────────────────────────────────

function StarRating({ value, onChange, max = 5, label }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="mb-6">
      <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
      <div className="flex gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
          >
            {star <= (hovered || value) ? (
              <StarSolid className="w-8 h-8 text-amber-400" />
            ) : (
              <StarIcon className="w-8 h-8 text-gray-300 hover:text-amber-200" />
            )}
          </button>
        ))}
      </div>
      {value > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          {value === 1 ? 'Poor' : value === 2 ? 'Fair' : value === 3 ? 'Good' : value === 4 ? 'Great' : 'Excellent'}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const DeepFikaFeedback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session');
  const applicationId = searchParams.get('participant');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [matchData, setMatchData] = useState(null);

  const [feedback, setFeedback] = useState({
    connection_rating: 0,      // How well did you connect?
    conversation_quality: 0,   // How engaging was the conversation?
    overall_satisfaction: 0,   // Overall experience
    would_match_again: null,   // true/false
    would_recommend: 0,        // 1-5
    connection_made: false,
    open_feedback: '',         // Open text
    what_worked_well: '',
    what_could_improve: '',
  });

  // ── Load session ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!sessionId || !applicationId) {
      setError('Invalid feedback link. Please use the link from your email.');
      setLoading(false);
      return;
    }
    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSession = async () => {
    try {
      const res = await callAdminAPI('get_deep_fika_feedback_session', {
        sessionId,
        applicationId,
      });

      if (res.success) {
        setSessionData(res.data.session);
        setMatchData(res.data.match);
        if (res.data.existingFeedback) setSubmitted(true);
      } else {
        throw new Error(res.error || 'Failed to load session');
      }
    } catch (err) {
      setError(err.message || 'Unable to load feedback form');
    }
    setLoading(false);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const canSubmit =
    feedback.connection_rating > 0 &&
    feedback.conversation_quality > 0 &&
    feedback.overall_satisfaction > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please complete the three ratings before submitting.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await callAdminAPI('submit_deep_fika_feedback', {
        sessionId,
        applicationId,
        feedback: {
          ...feedback,
          connection_made: feedback.would_match_again === true,
        },
      });
      if (res.success) {
        setSubmitted(true);
      } else {
        throw new Error(res.error || 'Failed to submit feedback');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Render helpers ────────────────────────────────────────────────────────────

  const matchName = matchData?.firstName || 'your match';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-stone-100 flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading your feedback form…</p>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-stone-100 flex items-center justify-center pt-20 px-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-8 text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to load feedback</h2>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <p className="text-xs text-gray-400 mb-4">
            If you think this is an error, email{' '}
            <a href="mailto:mark@connectco.app" className="underline">mark@connectco.app</a>
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2 bg-stone-800 text-white rounded-full text-sm font-semibold hover:bg-stone-900"
          >
            Back to ConnectCo
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-stone-100 flex items-center justify-center px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl max-w-md w-full overflow-hidden"
        >
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-5 text-center">
            <div className="text-white text-xl font-bold">Connect & Co · Deep Fika</div>
          </div>
          <div className="px-8 py-10 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
            >
              <CheckCircleIcon className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Thank you!</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Your feedback helps us make better matches. We appreciate you taking the time to share
              how your Deep Fika went.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left mb-6">
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">What's next</div>
              <ul className="text-xs text-emerald-800 space-y-1.5">
                <li>🫂 If you'd like another match, you're still in the pool</li>
                <li>☕ Join us at a monthly fika to keep expanding your circle</li>
                <li>📬 Stay tuned for future 1-on-1 match invitations</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/events')}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-colors"
            >
              See upcoming events
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-stone-100 px-4 pt-24 pb-16">
      <div className="max-w-lg mx-auto">
        {/* Header card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-5 text-center">
            <div className="text-white text-xl font-bold">Connect & Co · Deep Fika</div>
            <div className="text-emerald-100 text-sm mt-0.5">1-on-1 Feedback</div>
          </div>

          <div className="px-8 py-7">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              How did it go with {matchName}?
            </h2>
            <p className="text-gray-500 text-sm mb-7">
              Your honest feedback helps us make better matches for you and everyone in the community.
              It takes less than 2 minutes.
            </p>

            {/* ── Ratings ──────────────────────────────────────────────────── */}

            <StarRating
              label={'Connection — how well did you click with ' + matchName + '?'}
              value={feedback.connection_rating}
              onChange={v => setFeedback(f => ({ ...f, connection_rating: v }))}
            />

            <StarRating
              label="Conversation — how engaging and meaningful was the conversation?"
              value={feedback.conversation_quality}
              onChange={v => setFeedback(f => ({ ...f, conversation_quality: v }))}
            />

            <StarRating
              label="Overall experience — how was the Deep Fika overall?"
              value={feedback.overall_satisfaction}
              onChange={v => setFeedback(f => ({ ...f, overall_satisfaction: v }))}
            />

            {/* ── Would match again ─────────────────────────────────────────── */}

            <div className="mb-6">
              <div className="text-sm font-medium text-gray-700 mb-3">
                Would you like to be matched with someone new in the future?
              </div>
              <div className="flex gap-3">
                {[
                  { value: true,  label: '👍 Yes, match me again' },
                  { value: false, label: '✋ Not right now' },
                ].map(opt => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => setFeedback(f => ({ ...f, would_match_again: opt.value }))}
                    className={'flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ' + (
                      feedback.would_match_again === opt.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 text-gray-600 hover:border-emerald-300'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Open text ─────────────────────────────────────────────────── */}

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anything you want to share? <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={feedback.open_feedback}
                onChange={e => setFeedback(f => ({ ...f, open_feedback: e.target.value }))}
                placeholder={'What made the conversation enjoyable? Anything that could have gone better? Completely candid — this only goes to Mark.'}
                rows={4}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              />
            </div>

            {/* ── Error ─────────────────────────────────────────────────────── */}

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {error}
              </div>
            )}

            {/* ── Submit ────────────────────────────────────────────────────── */}

            <button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </button>

            {!canSubmit && (
              <p className="text-xs text-gray-400 text-center mt-2">
                Complete all three star ratings to submit
              </p>
            )}
          </div>
        </motion.div>

        <p className="text-center text-xs text-gray-400 mt-6">
          ConnectCo · Deep Fika · connectco.app
        </p>
      </div>
    </div>
  );
};

export default DeepFikaFeedback;
